import { ListComponentBaseProps, Table, toasterService } from '@hevoio/hevo-ui-toolkit';
import useAnalyticsTracker from '../../../../../../../analytics/hooks/useAnalyticsTracker';
import { WorkspaceTrackingActions } from '../../../../tracking';
import { getErrorMessageFromObj } from '../../../../../../../core/utils/request';
import { useMemo, useState } from 'react';
import styles from './WorkspaceListTable.module.scss';
import { useNavigate } from 'react-router-dom';
import { WorkspacesAPI } from '../../../../api/WorkspacesAPI';
import { IWorkspace } from '../../../../interfaces/workspace';
import { workspaceListColumns } from './WorkspaceListColumns';
import { DeleteWorkspaceModal } from '../DeleteWorkspaceModal/DeleteWorkspaceModal';
import useService from '../../../../../../../injector/hooks/useService';
import { UserService } from '../../../../../../../core/services/user.service';

interface WorkspaceListTableProps extends ListComponentBaseProps<IWorkspace[]> {
  onDeleteSuccess?: (workspaceId: string) => void;
}

export function WorkspaceListTable({
  data,
  tableProps,
  refresh,
  onDeleteSuccess
}: WorkspaceListTableProps) {
  const navigate = useNavigate();
  const userService = useService(UserService);
  const { eventTrack } = useAnalyticsTracker();
  const [workspaceToDelete, setWorkspaceToDelete] = useState<IWorkspace | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (workspace: IWorkspace) => {
    eventTrack({
      action: WorkspaceTrackingActions.DELETE_WORKSPACE_CLICK,
      properties: { workspaceId: workspace.workspace_id, workspaceName: workspace.name }
    });
    setWorkspaceToDelete(workspace);
  };

  const handleDeleteConfirm = async () => {
    if (!workspaceToDelete) return;

    setDeleting(true);
    try {
      const res = await WorkspacesAPI.deleteWorkspace(workspaceToDelete.workspace_id);
      toasterService.pop(
        'success',
        res.success_message || `Workspace "${workspaceToDelete.name}" deleted successfully`,
        undefined
      );
      eventTrack({
        action: WorkspaceTrackingActions.DELETE_WORKSPACE_SUCCESS,
        properties: {
          workspaceId: workspaceToDelete.workspace_id,
          workspaceName: workspaceToDelete.name
        }
      });
      userService.removeWorkspaceFromMemberships(workspaceToDelete.workspace_id);
      onDeleteSuccess?.(workspaceToDelete.workspace_id);
      refresh?.();
      setWorkspaceToDelete(null);
    } catch (err) {
      toasterService.pop(
        'error',
        undefined,
        getErrorMessageFromObj(err) || `Failed to delete workspace "${workspaceToDelete.name}"`
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteClose = () => {
    if (!deleting) {
      setWorkspaceToDelete(null);
    }
  };

  const { rows, columns } = useMemo(() => {
    const _columns = workspaceListColumns(handleDeleteClick);
    const _rows = data.map(workspace => ({
      data: workspace,
      id: workspace.workspace_id,
      tabIndex: 0,
      onClick: (w: IWorkspace) => {
        eventTrack({
          action: WorkspaceTrackingActions.NAVIGATE_TO_WORKSPACE,
          properties: { workspaceId: w.workspace_id, workspaceName: w.name }
        });
        navigate(`/team/workspaces/${w.workspace_id}`);
      }
    }));
    return { rows: _rows, columns: _columns };
  }, [data]);

  return (
    <>
      <Table
        columns={columns}
        rows={rows}
        {...tableProps}
        virtualization={{ enabled: true }}
        classes={{ ...(tableProps.classes || {}), containerClass: styles.virtualTable }}
      />
      <DeleteWorkspaceModal
        workspace={workspaceToDelete}
        deleting={deleting}
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteClose}
      />
    </>
  );
}
